import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import PetFoodCallout from "../components/PetFoodCallout";
import { Button } from "../components/ui/button";
import {
    Truck,
    HandHeart,
    Home as HomeIcon,
    Stethoscope,
    Utensils,
    Sparkles,
    Bell,
    Building2,
    Users,
    Scissors,
    Bot,
    Globe,
    ArrowRight,
    ShieldCheck,
    Languages,
    Rocket,
    KeyRound,
} from "lucide-react";

function ResourceCard({ icon: Icon, tone, title, body, stat, statLabel, to, cta, testid }) {
    return (
        <Link to={to} data-testid={testid} className="group card-warm rounded-2xl p-6 flex flex-col transition-all hover:-translate-y-0.5 hover:border-[var(--rl-primary)]">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 ${tone === "primary" ? "bg-[var(--rl-primary)] text-white" : "bg-[var(--rl-cream)] text-[var(--rl-text)]"}`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="font-serif text-2xl leading-tight">{title}</h3>
            <p className="mt-2 text-sm text-[var(--rl-text-2)] leading-relaxed flex-1">{body}</p>
            {stat !== undefined && (
                <div className="mt-4 flex items-baseline gap-2">
                    <span className="font-serif text-3xl text-[var(--rl-primary)]">{stat}</span>
                    <span className="eyebrow text-[var(--rl-text-3)]">{statLabel}</span>
                </div>
            )}
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--rl-primary)] group-hover:gap-2.5 transition-all">
                {cta} <ArrowRight className="w-4 h-4" />
            </span>
        </Link>
    );
}

export default function ResourcesForRescues() {
    const [s, setS] = useState(null);
    useEffect(() => {
        api.get("/stats/resources").then((r) => setS(r.data)).catch(() => {});
    }, []);

    const n = (v) => (s ? String(v) : "—");

    return (
        <div data-testid="resources-page">
            {/* Hero */}
            <section className="bg-[var(--rl-cream)] border-b border-[var(--rl-border)]">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-start">
                    <div className="lg:col-span-7">
                        <div className="eyebrow mb-4 inline-flex items-center gap-2 text-[var(--rl-primary)]">
                            <ShieldCheck className="w-3.5 h-3.5" /> For Rescues &amp; Shelters
                        </div>
                        <h1 className="font-serif text-[42px] leading-[1.05] sm:text-6xl tracking-tight">
                            Every resource<br />
                            <em className="not-italic text-[var(--rl-primary)]">your rescue needs,</em>{" "}
                            in one place.
                        </h1>
                        <p className="mt-6 text-lg text-[var(--rl-text-2)] max-w-xl leading-relaxed">
                            RescueLink is a working network — not a directory. Tap into transport routes, vetted foster homes, volunteer pools, veterinary partners, funding streams, and corporate donors that other rescues have already onboarded.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/auth?next=/dashboard"><Button className="btn-primary rounded-full h-12 px-6" data-testid="resources-join-btn">Join as a rescue <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
                            <Link to="/rescues"><Button variant="outline" className="rounded-full h-12 px-6 border-[var(--rl-border-strong)]">Meet the network</Button></Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">{n(s?.rescues)}</div><div className="eyebrow mt-1">rescue partners</div></div>
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">{n(s?.shelters)}</div><div className="eyebrow mt-1">shelters onboarded</div></div>
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">{n(s?.transport_legs)}</div><div className="eyebrow mt-1">active transport routes</div></div>
                        <div className="card-warm rounded-xl p-4"><div className="font-serif text-4xl text-[var(--rl-primary)]">{n(s?.countries)}</div><div className="eyebrow mt-1">countries covered</div></div>
                    </div>
                </div>
            </section>

            {/* Pet Food callout — receive variant */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-8">
                <PetFoodCallout variant="receive" />
            </section>

            {/* Register / API access banner */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-4" data-testid="resources-register-banner">
                <div className="rounded-2xl bg-[#1f1b16] text-[#f5e9d7] p-5 lg:p-6 grid lg:grid-cols-[auto_1fr_auto] gap-4 items-center">
                    <div className="w-11 h-11 rounded-full bg-[#f2b787] text-[#1f1b16] flex items-center justify-center shrink-0">
                        <Rocket className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="eyebrow text-[#f2b787] mb-0.5 inline-flex items-center gap-1.5"><KeyRound className="w-3 h-3" /> Not listed yet?</div>
                        <div className="font-serif text-xl lg:text-2xl leading-tight">Register your rescue &amp; get API access</div>
                        <p className="text-xs opacity-80 mt-1">List your org, post needs + resources, sync via REST API. Free for every rescue and shelter.</p>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Link to="/join" data-testid="resources-register-cta"><Button className="rounded-full h-10 px-4 bg-[#f2b787] text-[#1f1b16] hover:bg-[#e6a670] text-sm">Register</Button></Link>
                        <Link to="/developers"><Button variant="outline" className="rounded-full h-10 px-4 border-white/25 text-[#f5e9d7] hover:bg-white/10 hover:text-white text-sm">API docs</Button></Link>
                    </div>
                </div>
            </section>

            {/* Grid of resources */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16">
                <div className="mb-10">
                    <div className="eyebrow text-[var(--rl-primary)] mb-3">The Toolkit</div>
                    <h2 className="font-serif text-3xl sm:text-5xl tracking-tight max-w-3xl">Ten resource categories, live across the network.</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-grid">
                    <ResourceCard
                        icon={Truck} tone="primary"
                        title="Transport Network"
                        body="Post a pull request, watch drivers claim legs on a live route map, and coordinate with a leg-by-leg chat. Cross-border escorts already run through 17+ countries."
                        stat={n(s?.transport_legs)} statLabel="live routes"
                        to="/transport" cta="Open transport map" testid="resource-transport"
                    />
                    <ResourceCard
                        icon={HomeIcon}
                        title="Foster Network"
                        body="A verified pool of foster homes that filter themselves by size, age, and behavior tolerance. Signal a foster ask and get matches in hours, not weeks."
                        stat={n(s?.fosters)} statLabel="foster signals"
                        to="/foster" cta="Post a foster ask" testid="resource-foster"
                    />
                    <ResourceCard
                        icon={HandHeart}
                        title="Volunteer Pool"
                        body="Dog walkers, transport drivers, remote data workers, translators, event help. Post an opportunity and it appears in every relevant portal instantly."
                        stat={n(s?.volunteer_opportunities)} statLabel="open opps"
                        to="/volunteer" cta="Recruit volunteers" testid="resource-volunteer"
                    />
                    <ResourceCard
                        icon={Stethoscope} tone="primary"
                        title="Vet Partnerships"
                        body="Discounted spay/neuter, emergency consults, and rescue-rate clinics — vetted by other partner rescues. Rating + reviews included."
                        stat={n(s?.vet_partners)} statLabel="vet partners"
                        to="/services?category=veterinary" cta="Find a vet partner" testid="resource-vet"
                    />
                    <ResourceCard
                        icon={Utensils}
                        title="Food &amp; Supply Donors"
                        body="Pet-food banks, brand donation programs, community drives, and emergency assistance — all in one place. Post a food ask and get matched in 24 hours."
                        stat={n(s?.food_supply_partners)} statLabel="supply partners"
                        to="/pet-food" cta="Open the food network" testid="resource-supply"
                    />
                    <ResourceCard
                        icon={Building2} tone="primary"
                        title="Corporate Donors"
                        body="Stock gifts, DAF grants, crypto donations, employee-match programs, and long-term funders — matched to rescues by mission and geography."
                        stat={n(s?.stock_gifts)} statLabel="corporate gifts"
                        to="/corporate" cta="Corporate portal" testid="resource-corporate"
                    />
                    <ResourceCard
                        icon={Sparkles}
                        title="AI &amp; Data Tools"
                        body="AI matchmaking pairs adopters with your dogs. Rescue-analysis reports summarize dog personalities from your notes. Free for partner rescues."
                        to="/matchmaking" cta="See AI tools" testid="resource-ai"
                    />
                    <ResourceCard
                        icon={Languages}
                        title="Translation &amp; Global Escort"
                        body="Claude-powered bio translation in 6 languages so a Korean meat-farm survivor lands in a US foster home without missing a beat. Legal and airline escort contacts included."
                        to="/dogs" cta="See translated bios" testid="resource-translation"
                    />
                    <ResourceCard
                        icon={Bell} tone="primary"
                        title="Live Rescue Alerts"
                        body="Push urgent asks by reason (meat-trade, 48-hour, medical, transport-needed) directly to subscribed adopters, fosters, and drivers — worldwide."
                        to="/alerts" cta="Publish an alert" testid="resource-alerts"
                    />
                    <ResourceCard
                        icon={Users}
                        title="Community &amp; Playbooks"
                        body="Success-story feed, private rescue-org channel, and a shared playbook library for high-volume intake, ringworm protocols, and cross-border pulls."
                        to="/community" cta="Open community" testid="resource-community"
                    />
                    <ResourceCard
                        icon={Scissors}
                        title="Grooming &amp; Training"
                        body="Groomer partners for medical shaves and rehab, plus positive-reinforcement trainers who take at-risk cases at cost."
                        stat={`${n(s?.grooming_partners)} + ${n(s?.training_partners)}`} statLabel="partners"
                        to="/services" cta="Browse partners" testid="resource-groom-train"
                    />
                    <ResourceCard
                        icon={Bot} tone="primary"
                        title="Platform Uptime Fund"
                        body="The Robot Snacks micro-fund covers the platform's LLM + server costs so every rescue tool stays free. Fully transparent, community-fed."
                        to="/robot-snacks" cta="See Robot Snacks" testid="resource-snacks"
                    />
                </div>
            </section>

            {/* Onboarding strip */}
            <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-20">
                <div className="rounded-2xl bg-[var(--rl-primary)] text-white p-8 lg:p-12 grid lg:grid-cols-2 gap-8 items-center" data-testid="rescue-onboard-strip">
                    <div>
                        <div className="eyebrow text-white/80 mb-3 inline-flex items-center gap-2"><Globe className="w-3.5 h-3.5" /> Join the network</div>
                        <h2 className="font-serif text-3xl sm:text-4xl leading-tight">Your rescue, plugged into every resource above — in under 10 minutes.</h2>
                        <p className="mt-3 text-white/85 max-w-xl">Verified rescues get their own portal, dog intake tools, transport-leg publishing, alert broadcasting, and access to the corporate-donor pipeline.</p>
                    </div>
                    <div className="flex flex-wrap gap-3 lg:justify-end">
                        <Link to="/auth?next=/dashboard"><Button className="rounded-full h-12 px-6 bg-white text-[var(--rl-primary)] hover:bg-white/90" data-testid="rescue-signup-btn">Create rescue account</Button></Link>
                        <a href="mailto:partners@rescuelink.earth"><Button variant="outline" className="rounded-full h-12 px-6 border-white/60 text-white hover:bg-white/10 hover:text-white">Talk to a partner rep</Button></a>
                    </div>
                </div>
            </section>
        </div>
    );
}
